// admin-frontend/src/app/core/auth/auth.service.ts
// OIDC rewrite — mirrors frontend/src/app/core/auth/auth.service.ts (Plan 05)
// Key difference: post-callback navigation handled by AuthCallbackComponent → /customers (admin landing)
//
// Phase 10 (10-06):
//   - Replaced login(email, password) with OIDC redirect via OidcSecurityService.authorize()
//   - handleCallback() awaits checkAuth() + refreshCurrentUserFromServer()
//   - ensureAuthReady() handles app bootstrap (replaces storage-based approach)
//   - Signal shape preserved per D-03 — adminGuard reads role === 'admin'
//   - currentUser.id populated from /auth/me (ZITADEL JWT carries sub UUID, not DB int)

import { computed, inject, Injectable, signal } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { AppConfigService } from '@core/services/app-config.service';

export interface UserInfo {
  email: string;
  name: string;
  role: string;   // 'admin' | 'customer'
  id: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oidcService: OidcSecurityService = inject(OidcSecurityService);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly appConfig = inject(AppConfigService);

  // Signal shape preserved per D-03 — adminGuard and downstream components must not require changes
  readonly currentUser = signal<UserInfo | null>(null);
  readonly token = signal<string | null>(null);
  readonly isAuthenticated = computed(() => this.currentUser() !== null);
  readonly authReady = signal(false);

  // Live access token signal — kept up-to-date from OidcSecurityService.getAccessToken()
  // observable so that background token refreshes propagate to the next HTTP request
  // without requiring a page reload. Initialised to null; populated on first emission.
  private readonly _liveToken = signal<string | null>(null);

  constructor() {
    // Subscribe to the live OIDC token stream so that post-refresh tokens are captured
    // immediately. The `token` signal written by handleCallback()/ensureAuthReady() is
    // preserved for components that read it (per D-03); _liveToken is the interceptor
    // source (per D-01, D-02).
    this.oidcService.getAccessToken().subscribe((t: string) => {
      this._liveToken.set(t || null);
    });
  }

  /**
   * Returns the live OIDC access token by reading the _liveToken signal, which is kept
   * up-to-date from OidcSecurityService.getAccessToken() (an Observable in v21).
   * Per D-01: thin pass-through, no side effects, no signal writes to `token`.
   * Per D-03: does NOT mutate the `token` signal — that stays for component consumers.
   * Returns null when OidcSecurityService has no active session (empty string normalised).
   */
  getAccessToken(): string | null {
    // _liveToken is set by an async subscription; fall back to token() which
    // handleCallback/ensureAuthReady set synchronously so the first API call
    // after the OIDC callback is not sent without a Bearer header.
    return this._liveToken() ?? this.token();
  }

  /** Initiates OIDC redirect to ZITADEL login UI (replaces login(email, password)). */
  login(): void {
    this.oidcService.authorize();
  }

  logout(): void {
    this.oidcService.logoff().subscribe({
      complete: () => {
        this.currentUser.set(null);
        this.token.set(null);
        void this.router.navigate(['/login']);
      },
    });
  }

  /**
   * Called by the /auth/callback component after OIDC code exchange.
   * Awaits checkAuth() + refreshCurrentUserFromServer() before navigation.
   * id is populated from /auth/me — ZITADEL JWT carries sub (UUID), not DB int.
   */
  async handleCallback(): Promise<void> {
    const { accessToken, isAuthenticated } = await firstValueFrom(
      this.oidcService.checkAuth()
    );
    if (isAuthenticated && accessToken) {
      this.token.set(accessToken);
      // id comes from /auth/me — refreshCurrentUserFromServer is awaited to close the id:0 window
      await this.refreshCurrentUserFromServer();
    }
    this.authReady.set(true);
  }

  /**
   * Called by withAppInitializerAuthCheck() at bootstrap — confirms OIDC session before route activation.
   * Guards call this to ensure auth state is resolved before checking isAuthenticated().
   */
  async ensureAuthReady(): Promise<UserInfo | null> {
    if (this.authReady()) return this.currentUser();
    const { accessToken, isAuthenticated } = await firstValueFrom(
      this.oidcService.checkAuth()
    );
    if (isAuthenticated && accessToken) {
      this.token.set(accessToken);
      await this.refreshCurrentUserFromServer();
    }
    this.authReady.set(true);
    return this.currentUser();
  }

  private async refreshCurrentUserFromServer(): Promise<void> {
    try {
      const user = await firstValueFrom(
        this.http.get<UserInfo & { is_active?: boolean }>(`${this.appConfig.apiUrl}/auth/me`)
      );
      this.currentUser.set({
        email: user.email,
        name: user.name,
        role: user.role,
        id: user.id,
      });
    } catch (error: unknown) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.logout();
      }
    }
  }
}
